import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import debounce from 'lodash.debounce';

import { getLenis } from '../components/lenis-scroll';
import {
    armWillChange,
    disarmWillChange,
    prefersReducedMotion,
} from './_shared';

gsap.registerPlugin(ScrollTrigger);

const SELECTOR = '[data-mda-gsap-slide-under]';
const CONTENT_SELECTOR = '[data-mda-gsap-slide-under-content]';
const HEADER_LAYOUT_CHANGE_EVENT = 'mda-header-layout-change';
const COMPLETE_PROGRESS = 0.999;
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

function isSlideUnderEnabled() {
    return (
        typeof window !== 'undefined' &&
        window.matchMedia(DESKTOP_MEDIA_QUERY).matches
    );
}

function getScrub(element) {
    const value = element?.dataset?.mdaGsapSlideUnderScrub;

    if (value === undefined || value === '') {
        return true;
    }

    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed) ? parsed : true;
}

function getContentTarget(element) {
    return element.querySelector(CONTENT_SELECTOR) ?? element;
}

const MARGIN_TO_PADDING_CLASS = {
    'margin-b-120': 'pb-120',
    'margin-b-96': 'pb-96',
    'margin-b-64': 'pb-64',
};

function getLastSectionMarginClass(section) {
    return (
        Object.keys(MARGIN_TO_PADDING_CLASS).find(marginClass =>
            section.classList.contains(marginClass)
        ) ?? null
    );
}

function relocateLastSectionMargin(main) {
    const lastSection = main?.lastElementChild;

    if (!lastSection || lastSection.tagName !== 'SECTION') {
        return null;
    }

    const marginClass = getLastSectionMarginClass(lastSection);

    if (!marginClass) {
        return null;
    }

    const paddingClass = MARGIN_TO_PADDING_CLASS[marginClass];

    return {
        main,
        lastSection,
        marginClass,
        paddingClass,
        apply() {
            lastSection.classList.remove(marginClass);
            main.classList.add(paddingClass);
        },
        revert() {
            lastSection.classList.add(marginClass);
            main.classList.remove(paddingClass);
        },
    };
}

function completeAnimation(content) {
    gsap.set(content, { yPercent: 0 });
}

function refreshMeasurements(animation) {
    const { content, tween } = animation;
    const progress = tween.scrollTrigger?.progress ?? 0;

    if (progress >= COMPLETE_PROGRESS) {
        completeAnimation(content);
    } else {
        gsap.set(content, { yPercent: -100 * (1 - progress) });
    }

    tween.scrollTrigger?.refresh();
}

function whenImageReady(element, callback) {
    if (element.complete && element.naturalHeight > 0) {
        callback();
        return null;
    }

    const handler = () => callback();

    element.addEventListener('load', handler, { once: true });

    return handler;
}

function bindMeasurementRefresh(animation) {
    const { content } = animation;
    const refresh = () => refreshMeasurements(animation);
    const pendingLoadHandlers = [];

    content.querySelectorAll('img').forEach(img => {
        const loadHandler = whenImageReady(img, refresh);

        if (loadHandler) {
            pendingLoadHandlers.push({ element: img, loadHandler });
        }
    });

    if (document.fonts?.ready) {
        document.fonts.ready.then(refresh);
    }

    const onLoad = () => refresh();

    window.addEventListener('load', onLoad, { once: true });

    const resizeObserver = new ResizeObserver(debounce(refresh, 100));

    resizeObserver.observe(content);

    return {
        refresh,
        pendingLoadHandlers,
        onLoad,
        resizeObserver,
    };
}

function setupPreviousSibling(element) {
    const prev = element.previousElementSibling;

    if (!prev) {
        return { main: null, marginRelocation: null };
    }

    const marginRelocation = relocateLastSectionMargin(prev);

    marginRelocation?.apply();

    gsap.set(prev, {
        position: 'relative',
        zIndex: 1,
    });

    return { main: prev, marginRelocation };
}

function createSlideUnderAnimation(element) {
    const content = getContentTarget(element);
    const { main, marginRelocation } = setupPreviousSibling(element);

    gsap.set(element, {
        position: 'relative',
    });
    armWillChange(content);

    const tween = gsap.fromTo(
        content,
        { yPercent: -100 },
        {
            yPercent: 0,
            ease: 'none',
            scrollTrigger: {
                trigger: element,
                start: 'top bottom',
                end: 'bottom bottom',
                scrub: getScrub(element),
                invalidateOnRefresh: true,
                onUpdate(self) {
                    if (self.progress >= COMPLETE_PROGRESS) {
                        completeAnimation(content);
                    }
                },
                onLeave(self) {
                    if (
                        self.direction === 1 &&
                        self.progress >= COMPLETE_PROGRESS
                    ) {
                        completeAnimation(content);
                    }
                },
            },
        }
    );

    return { element, content, main, marginRelocation, tween };
}

function bindLenisEndClamp(animations) {
    const lenis = getLenis();

    if (!lenis) {
        return null;
    }

    const handleScroll = debounce(({ scroll, limit }) => {
        if (scroll < limit - 2) {
            return;
        }

        animations.forEach(({ content }) => {
            completeAnimation(content);
        });
        ScrollTrigger.refresh();
    }, 100);

    lenis.on('scroll', handleScroll);

    return () => {
        lenis.off('scroll', handleScroll);
        handleScroll.cancel();
    };
}

function createSlideUnderCoordinator(elements) {
    const media = gsap.matchMedia();

    media.add(DESKTOP_MEDIA_QUERY, () => {
        const activeCoordinator = mountSlideUnderCoordinator(elements);

        return () => {
            activeCoordinator.destroy();
        };
    });

    return {
        elements,
        media,
        destroy() {
            media.revert();
        },
    };
}

function mountSlideUnderCoordinator(elements) {
    const animations = elements.map(element =>
        createSlideUnderAnimation(element)
    );
    const refreshBindings = animations.map(animation =>
        bindMeasurementRefresh(animation)
    );

    const refreshAll = debounce(() => {
        animations.forEach(animation => refreshMeasurements(animation));
        ScrollTrigger.refresh();
    }, 150);

    const onHeaderLayoutChange = () => {
        refreshAll();
    };

    window.addEventListener(HEADER_LAYOUT_CHANGE_EVENT, onHeaderLayoutChange);

    const unbindLenisEndClamp = bindLenisEndClamp(animations);

    ScrollTrigger.refresh();

    return {
        elements,
        animations,
        refreshBindings,
        destroy() {
            window.removeEventListener(
                HEADER_LAYOUT_CHANGE_EVENT,
                onHeaderLayoutChange
            );
            refreshAll.cancel();
            unbindLenisEndClamp?.();

            refreshBindings.forEach(
                ({ pendingLoadHandlers, onLoad, resizeObserver }) => {
                    pendingLoadHandlers.forEach(({ element, loadHandler }) => {
                        element.removeEventListener('load', loadHandler);
                    });
                    window.removeEventListener('load', onLoad);
                    resizeObserver.disconnect();
                }
            );

            animations.forEach(
                ({ element, content, main, marginRelocation, tween }) => {
                    tween.scrollTrigger?.kill();
                    tween.kill();

                    gsap.set(element, { clearProps: 'zIndex,position' });
                    gsap.set(content, { clearProps: 'transform' });

                    marginRelocation?.revert();

                    if (main) {
                        gsap.set(main, {
                            clearProps: 'zIndex,position',
                        });
                    }
                }
            );

            disarmWillChange(animations.map(({ content }) => content));
        },
    };
}

export class SlideUnder {
    static selector = SELECTOR;

    constructor(element, options = {}) {
        if (!element) {
            throw new Error('SlideUnder requires an element.');
        }

        this.element = element;
        this.options = options;
        this.coordinator = null;

        if (prefersReducedMotion() || !isSlideUnderEnabled()) {
            gsap.set(getContentTarget(element), { yPercent: 0 });
            return;
        }

        this.coordinator = createSlideUnderCoordinator([element]);
    }

    destroy() {
        this.coordinator?.destroy();
        this.coordinator = null;
    }

    static init(root = document) {
        if (prefersReducedMotion() || !root?.querySelectorAll) {
            return [];
        }

        const elements = Array.from(root.querySelectorAll(SELECTOR));

        if (!elements.length) {
            return [];
        }

        const coordinator = createSlideUnderCoordinator(elements);

        return coordinator ? [coordinator] : [];
    }
}
